import React, { useEffect, useState } from 'react';
import { API } from '@/App';

/**
 * Pollen Dots branded footer — visual continuity with the live
 * pollendots.com footer (maroon background, ivory text, Larken-style serif
 * headers, Instagram + YouTube SVGs at 80% opacity, phone + WhatsApp +
 * Quick Links columns). Lightweight rebuild — no remote fonts or animation
 * libraries — so public share pages stay snappy.
 */

const QUICK_LINKS = [
  { label: 'About', href: 'https://pollendots.com/about', testid: 'footer-link-about' },
  { label: 'Travel', href: 'https://pollendots.com/travel', testid: 'footer-link-travel' },
  { label: 'Connect', href: 'https://pollendots.com/connect', testid: 'footer-link-connect' },
  { label: 'Care', href: 'https://pollendots.com/care', testid: 'footer-link-care' },
  { label: 'Team', href: 'https://pollendots.com/about#team', testid: 'footer-link-team' },
  { label: 'Services', href: 'https://pollendots.com/services', testid: 'footer-link-services' },
  { label: 'Contact', href: 'https://pollendots.com/contact', testid: 'footer-link-contact' },
  { label: 'The Caravan Network', href: 'https://pollendots.com/thecaravannetwork', testid: 'footer-link-caravan' },
];

const MORE_LINKS = [
  { label: 'Privacy Policy', href: 'https://pollendots.com/privacy-policy', testid: 'footer-link-privacy' },
  { label: 'Terms & Conditions', href: 'https://pollendots.com/terms-and-conditions', testid: 'footer-link-terms' },
];

// Reuses the live site's exact Instagram glyph for visual parity.
const InstagramGlyph = (props) => (
  <svg viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <g opacity="0.8">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.51 0C4.05 0 2.65.58 1.61 1.61.58 2.65 0 4.05 0 5.51v14.69C0 21.66.58 23.07 1.61 24.1c1.04 1.03 2.44 1.61 3.9 1.61h14.69c1.46 0 2.86-.58 3.9-1.61 1.03-1.04 1.61-2.44 1.61-3.9V5.51c0-1.46-.58-2.86-1.61-3.9C23.07.58 21.66 0 20.21 0H5.51zm16.28 5.52c0 .42-.17.83-.46 1.13-.3.3-.7.46-1.12.46-.42 0-.83-.17-1.12-.46-.3-.3-.46-.7-.46-1.13 0-.42.17-.83.46-1.13.3-.3.7-.46 1.12-.46.42 0 .82.17 1.12.46.3.3.46.7.46 1.13zM12.86 8.45c-1.17 0-2.29.46-3.11 1.29-.83.83-1.29 1.94-1.29 3.12 0 1.17.46 2.29 1.29 3.12.82.82 1.94 1.28 3.11 1.28 1.17 0 2.29-.46 3.11-1.28.83-.83 1.29-1.95 1.29-3.12 0-1.18-.46-2.29-1.29-3.12-.82-.83-1.94-1.29-3.11-1.29zm-6.52 4.41c0-1.73.69-3.39 1.91-4.61 1.22-1.22 2.88-1.91 4.61-1.91 1.73 0 3.39.69 4.61 1.91 1.22 1.22 1.91 2.88 1.91 4.61 0 1.73-.69 3.39-1.91 4.61-1.22 1.22-2.88 1.91-4.61 1.91-1.73 0-3.39-.69-4.61-1.91-1.22-1.22-1.91-2.88-1.91-4.61z"
        fill="#FFFAF0"
      />
    </g>
  </svg>
);

const YouTubeGlyph = (props) => (
  <svg viewBox="0 0 34 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <path
      opacity="0.8"
      d="M33.99 8.46c.08-2.22-.41-4.43-1.4-6.41-.68-.81-1.62-1.36-2.66-1.55C25.63.11 21.32-.05 17 .02 12.7-.06 8.4.1 4.12.48c-.85.16-1.63.55-2.26 1.15C.48 2.92.32 5.12.17 6.98c-.23 3.35-.23 6.71 0 10.06.05 1.05.2 2.09.46 3.1.19.78.57 1.51 1.1 2.11.63.63 1.43 1.05 2.3 1.21 3.33.41 6.69.58 10.04.51 5.41.08 10.15 0 15.76-.43.89-.16 1.71-.58 2.36-1.21.43-.43.76-.97.94-1.55.55-1.7.82-3.49.8-5.28.06-.87.06-6.11.06-7.04zM13.51 16.43V6.83l9.14 4.83c-2.56 1.43-5.95 3.04-9.14 4.78z"
      fill="#FFFAF0"
    />
  </svg>
);

const PhoneGlyph = (props) => (
  <svg viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <path
      opacity="0.8"
      d="M20 26c-1.1 0-2.6-.4-4.9-1.7-2.8-1.6-5-3.1-7.7-6-2.7-2.8-4-4.6-5.8-8C-.4 6.4-.1 4.3.3 3.4.8 2.4 1.5 1.8 2.3 1.2c.5-.3 1-.6 1.6-.9 0 0 .1 0 .1-.1.3-.1.7-.3 1.2-.1.4.1.7.4 1.2.9 1 1 2.4 3.3 2.9 4.5.3.8.6 1.3.6 1.8 0 .7-.3 1.2-.7 1.7l-.2.3c-.4.6-.5.7-.5 1 .1.6 1 2.4 2.6 4 1.5 1.6 3.2 2.5 3.8 2.6.3.1.5 0 1.1-.5l.3-.2c.6-.5 1.1-.8 1.7-.8.5 0 1 .2 1.8.6 1 .5 3.3 2 4.3 3 .5.5.8.8.9 1.2.2.6 0 1-.1 1.3-.1.5-.4 1-.6 1.5-.6.9-1.2 1.6-2.2 2.1-.5.3-1.1.4-1.7.4z"
      fill="#FFFAF0"
    />
  </svg>
);

const WhatsAppGlyph = (props) => (
  <svg viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" {...props}>
    <path
      opacity="0.8"
      d="M11 0c1.7 0 3.4.4 4.9 1.1 1.5.8 2.8 1.9 3.8 3.2 1 1.4 1.7 2.9 2 4.6.3 1.7.3 3.4-.2 5-.4 1.6-1.2 3.1-2.4 4.4-1.1 1.3-2.5 2.3-4.1 2.9-1.6.6-3.3.9-5 .8-1.7-.1-3.3-.6-4.8-1.5L.9 22c-.1 0-.2 0-.4-.1-.1-.1-.2-.2-.3-.3-.1-.1-.1-.2-.1-.4l1.5-4.5C.5 14.9 0 13 0 11.1c0-1.9.5-3.8 1.4-5.5C2.4 3.9 3.8 2.5 5.5 1.5 7.2.5 9.1 0 11 0zm-3.83 5.51c-.14 0-.28.04-.4.1-.13.07-.24.16-.32.27-.25.29-.95 1-.95 2.43 0 1.43.97 2.82 1.1 3.02.14.19 1.91 3.13 4.63 4.4.5.23 1.02.43 1.54.61.65.22 1.24.19 1.71.12.52-.08 1.6-.7 1.83-1.39.22-.67.22-1.26.15-1.38-.07-.12-.25-.2-.52-.34-.27-.15-1.6-.85-1.85-.95-.25-.1-.43-.15-.61.14-.18.29-.7.95-.86 1.14-.16.19-.32.22-.59.07-.27-.14-1.14-.45-2.18-1.45-.8-.77-1.35-1.73-1.5-2.02-.16-.29-.02-.45.12-.59.13-.13.29-.34.43-.51.14-.17.19-.29.29-.49.1-.19.05-.36-.02-.51-.08-.14-.61-1.46-.84-2-.22-.53-.45-.46-.61-.46h-.52z"
      fill="#FFFAF0"
    />
  </svg>
);

const PollenDotsContactFooter = () => {
  const [build, setBuild] = useState(null);
  useEffect(() => {
    let on = true;
    fetch(`${API}/version`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (on) setBuild(d); })
      .catch(() => {});
    return () => { on = false; };
  }, []);
  const stamp = build
    ? `${build.git_sha} \u00B7 ${String(build.build_time || '').replace('T', ' ').replace(/:\d\d\+.*$/, '')} UTC`
    : '';

  return (
    <footer
      className="mt-12 -mx-4 sm:-mx-6 lg:-mx-8 bg-[#581C2B] text-[#FFFAF0] relative overflow-hidden"
      style={{ fontFamily: '"Inter", system-ui, -apple-system, sans-serif' }}
      data-testid="pollen-contact-footer"
    >
      {/* Tibetan prayer-flag banner mirroring the live site's visual motif */}
      <img
        src="https://pollendots.com/assets/images/general-details/flags.webp"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="w-full block relative z-[1]"
      />
      {/* Mountain silhouette at the bottom of the footer (decorative) */}
      <img
        src="https://pollendots.com/assets/images/footer/mountains.webp"
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="w-full absolute left-0 bottom-0 z-0 pointer-events-none select-none"
      />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-10 sm:pt-12 pb-10 relative z-[2]">
        {/* Plan-your-journey CTA card */}
        <a
          href="https://wa.me/916295484375?text=Hey%2C+Team+Pollen+Dots%21"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-plan-cta"
          className="block relative rounded-3xl border-2 border-dashed border-[#FFFAF0]/30 bg-[#4A1624] p-6 sm:p-8 mb-10 max-w-md hover:border-[#FFFAF0]/60 transition-colors"
        >
          <svg viewBox="0 0 59 50" className="w-10 h-auto absolute -top-5 right-10" aria-hidden="true">
            <rect y="22" width="59" height="9" fill="#4A1624" />
            <path d="M32.89 3.76c0 2.08-1.25 11.76-3.8 21.17-2.54-9.41-3.79-19.09-3.79-21.17 0-2.08 1.7-3.76 3.79-3.76 2.1 0 3.8 1.68 3.8 3.76z" fill="#F8CD55" />
            <path d="M32.89 46.15c0-2.08-1.25-11.76-3.8-21.17-2.54 9.41-3.79 19.09-3.79 21.17 0 2.08 1.7 3.76 3.79 3.76 2.1 0 3.8-1.68 3.8-3.76z" fill="#F8CD55" />
            <path d="M13.06 11.15c1.8 1.04 9.56 6.96 16.43 13.87-9.42-2.5-18.43-6.26-20.23-7.3-1.8-1.04-2.41-3.36-1.36-5.17 1.05-1.82 3.36-2.45 5.16-1.4z" fill="#F8CD55" />
            <path d="M13.06 38.77c1.8-1.04 9.56-6.96 16.43-13.87-9.42 2.5-18.43 6.26-20.23 7.3-1.8 1.04-2.41 3.36-1.36 5.17 1.05 1.82 3.36 2.44 5.16 1.4z" fill="#F8CD55" />
            <path d="M45.94 11.15c-1.8 1.04-9.56 6.96-16.43 13.87 9.42-2.5 18.43-6.26 20.23-7.3 1.8-1.04 2.41-3.36 1.36-5.17-1.05-1.82-3.36-2.45-5.16-1.4z" fill="#F8CD55" />
            <path d="M45.94 38.77c-1.8-1.04-9.56-6.96-16.43-13.87 9.42 2.5 18.43 6.26 20.23 7.3 1.8 1.04 2.41 3.36 1.36 5.17-1.05 1.82-3.36 2.44-5.16 1.4z" fill="#F8CD55" />
          </svg>
          <p
            className="text-2xl sm:text-3xl text-[#FFFAF0] leading-tight"
            style={{ fontFamily: '"Crimson Pro", "Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
          >
            Let&apos;s help you<br />plan your journey
          </p>
          <p className="text-sm text-[#FFFAF0]/70 mt-3 max-w-sm">
            Explore responsibly, discover offbeat places, guided by locals, rooted in culture.
          </p>
          <span className="mt-5 inline-flex items-center gap-3 rounded-full bg-white text-[#030922] pl-4 pr-1.5 py-1.5 font-mono text-sm uppercase tracking-wider">
            Let&apos;s Go
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-full border border-dashed border-[#030922]">
              <svg viewBox="0 0 25 26" className="w-3.5 h-auto" aria-hidden="true">
                <g fill="#F3BAA8">
                  <path d="M14.48 2.54c0 1.04-.64 5.89-1.94 10.61-1.3-4.72-1.94-9.57-1.94-10.61 0-1.04.87-1.89 1.94-1.89 1.07 0 1.94.85 1.94 1.89z" />
                  <path d="M21.13 6.24c-.92.52-4.88 3.49-8.38 6.95 4.81-1.25 9.4-3.13 10.32-3.66.92-.52 1.23-1.68.7-2.59-.54-.91-1.72-1.23-2.64-.7z" />
                  <path d="M21.13 20.09c-.92-.52-4.88-3.49-8.38-6.95 4.81 1.25 9.4 3.13 10.32 3.66.92.52 1.23 1.68.7 2.59-.54.91-1.72 1.22-2.64.7z" />
                  <path d="M14.48 23.78c0-1.04-.64-5.89-1.94-10.61-1.3 4.72-1.94 9.57-1.94 10.61 0 1.04.87 1.89 1.94 1.89 1.07 0 1.94-.85 1.94-1.89z" />
                  <path d="M4.36 20.09c.92-.52 4.88-3.49 8.38-6.95-4.81 1.25-9.4 3.13-10.32 3.66-.92.52-1.23 1.68-.7 2.59.54.91 1.72 1.22 2.64.7z" />
                  <path d="M4.36 6.24c.92.52 4.88 3.49 8.38 6.95-4.81-1.25-9.4-3.13-10.32-3.66-.92-.52-1.23-1.68-.7-2.59.54-.91 1.72-1.23 2.64-.7z" />
                </g>
              </svg>
            </span>
          </span>
        </a>

        <div className="grid grid-cols-2 gap-8 sm:gap-10">
          {/* Quick Links */}
          <div>
            <h3
              className="text-xl sm:text-2xl text-[#FFFAF0] mb-4"
              style={{ fontFamily: '"Crimson Pro", "Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              Quick Links
            </h3>
            <ul className="space-y-2 text-sm text-[#FFFAF0]/80">
              {QUICK_LINKS.map(l => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#FFFAF0] hover:underline underline-offset-4 transition-colors"
                    data-testid={l.testid}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* More + Social */}
          <div>
            <h3
              className="text-xl sm:text-2xl text-[#FFFAF0] mb-4"
              style={{ fontFamily: '"Crimson Pro", "Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
            >
              More
            </h3>
            <ul className="space-y-2 text-sm text-[#FFFAF0]/80">
              {MORE_LINKS.map(l => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#FFFAF0] hover:underline underline-offset-4 transition-colors"
                    data-testid={l.testid}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-3 mt-6">
              <a
                href="https://www.instagram.com/pollendots/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pollen Dots on Instagram"
                data-testid="footer-instagram"
                className="hover:opacity-100 opacity-90 transition-opacity"
              >
                <InstagramGlyph className="w-6 h-6" />
              </a>
              <a
                href="https://www.youtube.com/@pollendots"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pollen Dots on YouTube"
                data-testid="footer-youtube"
                className="hover:opacity-100 opacity-90 transition-opacity"
              >
                <YouTubeGlyph className="w-7 h-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-10">
          <h3
            className="text-xl sm:text-2xl text-[#FFFAF0] mb-4"
            style={{ fontFamily: '"Crimson Pro", "Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
          >
            Contact Info
          </h3>
          <ul className="space-y-3 text-sm text-[#FFFAF0]/80">
            <li>
              <a
                href="tel:+916295484375"
                className="flex items-center gap-2.5 hover:text-[#FFFAF0] transition-colors"
                data-testid="footer-phone"
              >
                <PhoneGlyph className="w-4 h-4 shrink-0" />
                <span>+91 629-548-4375</span>
              </a>
            </li>
            <li>
              <a
                href="https://wa.me/916295484375?text=Hey%2C+Team+Pollen+Dots%21"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 hover:text-[#FFFAF0] transition-colors"
                data-testid="footer-whatsapp"
              >
                <WhatsAppGlyph className="w-4 h-4 shrink-0" />
                <span>+91 629-548-4375</span>
              </a>
            </li>
            <li>
              <a
                href="mailto:travel@pollendots.com"
                className="hover:text-[#FFFAF0] transition-colors"
                data-testid="footer-email"
              >
                travel@pollendots.com
              </a>
            </li>
          </ul>
        </div>

        {/* Brand lock-up (footer variant: yellow DD + white wordmark) */}
        <div className="mt-16 sm:mt-20 flex flex-col items-center text-center gap-3">
          <a
            href="https://www.pollendots.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
            data-testid="footer-wordmark"
          >
            <img
              src="https://pollendots.com/assets/images/footer/logoo.png"
              alt="Pollen Dots"
              className="h-10 sm:h-12 w-auto block"
              loading="lazy"
            />
          </a>
          <p className="text-[11px] text-[#FFFAF0]/55 mt-4">
            Copyright {new Date().getFullYear()} @ Pollendots
          </p>
          {stamp && (
            <p className="font-mono text-[10px] tracking-wider text-[#FFFAF0]/40" data-testid="pollen-build-stamp">
              build {stamp}
            </p>
          )}
        </div>
      </div>
    </footer>
  );
};

export default PollenDotsContactFooter;
